import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SnackbarSeverityEnum } from '../../../enums/snackbar-severity-enum';
import { useGetInviteTokenQuery } from '../../../store/apiSlice/auth/authApi';
import { useDeepCompareMemoize } from '../../useDeepMemoize';
import { ERROR_MSGS } from '../../../constants/registration/error.messages';

interface IUseInviteTokenValidation {
  showError: (message: string, severity: SnackbarSeverityEnum) => void;
}

export const useInviteTokenValidation = (
  options: IUseInviteTokenValidation
) => {
  // Stabilize the options object across renders using deep comparison.
  const { showError } = useDeepCompareMemoize(options);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const inviteToken = searchParams.get('inviteToken');

  const {
    // data: inviteTokenData,
    error: inviteTokenError,
    isError: isInviteTokenError,
    isLoading: isInviteTokenLoading,
  } = useGetInviteTokenQuery(
    { inviteToken: inviteToken ?? '' },
    { skip: !inviteToken }
  );

  const handleInviteTokenError = useCallback(
    ({
      inviteTokenError,
      showError,
    }: {
      inviteTokenError: FetchBaseQueryError | SerializedError | undefined;
      showError: IUseInviteTokenValidation['showError'];
    }) => {
      if (inviteTokenError && 'status' in inviteTokenError) {
        const { status } = inviteTokenError;
        // TODO: by the way we don't handle token expiration at all
        if (status === 404) {
          showError(
            ERROR_MSGS.INVITATION_TOKEN_NOT_FOUND,
            SnackbarSeverityEnum.ERROR
          );
          return;
        }
      }

      showError(ERROR_MSGS.UNKNOWN_ERROR, SnackbarSeverityEnum.ERROR);
    },
    []
  );

  const validateInviteToken = useCallback(
    ({
      inviteToken,
      isInviteTokenError,
      inviteTokenError,
      showError,
    }: {
      inviteToken: string | null;
      isInviteTokenError: boolean;
      inviteTokenError: FetchBaseQueryError | SerializedError | undefined;
      showError: IUseInviteTokenValidation['showError'];
    }) => {
      if (!inviteToken) {
        return showError(
          ERROR_MSGS.INVITATION_TOKEN_MISSING,
          SnackbarSeverityEnum.ERROR
        );
      }

      if (isInviteTokenError) {
        handleInviteTokenError({ inviteTokenError, showError });
      }
    },
    [handleInviteTokenError]
  );

  useEffect(() => {
    if (!inviteToken || isInviteTokenError) {
      validateInviteToken({
        inviteToken,
        isInviteTokenError,
        inviteTokenError,
        showError,
      });
    }
  }, [
    inviteToken,
    isInviteTokenError,
    inviteTokenError,
    showError,
    validateInviteToken,
  ]);

  return { isInviteTokenLoading, validateInviteToken, handleInviteTokenError };
};
